import React, { useEffect, useMemo, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';

import Highlighter from '@components/HighlightWords/index';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle/index';
import ShadowBox from '@components/Restyle/ShadowBox';
import Icon from '@components/shared/Icon/Icon';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { ChecklistTemplate } from '@graphql/generated';
import theme from '@themes/theme';

export type ChecklistTemplateX = (ChecklistTemplate | undefined) & {
  itemsChecked: string[];
};

interface ChecklistItemProps {
  item: ChecklistTemplateX;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onToggleItemChecked: (checklistId: string, itemId: string) => void;
  onToggleItemCheckedAll: (checklistId: string, isClear?: boolean) => void;
  highlightStr?: string;
  marginHorizontal?: any;
}

const AnimatedBox = Animated.createAnimatedComponent(Box);

export const ChecklistTemplateItem: React.FC<ChecklistItemProps> = React.memo(
  ({
    item,
    onToggleFavorite,
    onToggleItemChecked,
    onToggleItemCheckedAll,
    isExpanded,
    onToggleExpand,
    highlightStr,
    marginHorizontal = 'm',
  }) => {
    const selectedCount = useMemo(
      () => item.itemsChecked.length,
      [item.itemsChecked.length]
    );
    const totalItems = useMemo(
      () => item.checklistTemplateItems.length,
      [item.checklistTemplateItems.length]
    );

    const height = useSharedValue(0);
    const opacity = useSharedValue(0);
    const translateY = useSharedValue(-10);

    useEffect(() => {
      if (isExpanded) {
        height.value = withTiming(totalItems * 40 + theme.spacing.s + 32, {
          duration: 300,
        });
        opacity.value = withTiming(1, { duration: 300 });
        translateY.value = withTiming(0, { duration: 300 });
      } else {
        height.value = withTiming(0, { duration: 300 });
        opacity.value = withTiming(0, { duration: 300 });
        translateY.value = withTiming(-10, { duration: 300 });
      }
    }, [isExpanded, totalItems]);

    const animatedContainerStyle = useAnimatedStyle(() => ({
      height: height.value,
      overflow: 'hidden' as const,
    }));

    const animatedContentStyle = useAnimatedStyle(() => ({
      transform: [{ translateY: translateY.value }],
      opacity: opacity.value,
    }));

    const handleToggleFavorite = useCallback(() => {
      onToggleFavorite(item.id);
    }, [onToggleFavorite, item.id]);

    const handleToggleExpand = useCallback(() => {
      onToggleExpand(item.id);
    }, [onToggleExpand, item.id]);

    const handleToggleItemChecked = useCallback(
      (itemId: string) => {
        onToggleItemChecked(item.id, itemId);
      },
      [onToggleItemChecked, item.id]
    );

    const renderedItems = useMemo(
      () =>
        item.checklistTemplateItems.map(
          ({ id, checklistTemplateItemName: itemTitle }) => (
            <ChecklistSubItem
              key={id}
              id={id}
              itemTitle={itemTitle}
              isChecked={item.itemsChecked.includes(id)}
              onToggleItemChecked={handleToggleItemChecked}
            />
          )
        ),
      [item.checklistTemplateItems, item.itemsChecked, handleToggleItemChecked]
    );

    const onPressSelectAll = () => {
      if (
        selectedCount === 0 ||
        (selectedCount > 0 && selectedCount < totalItems)
      ) {
        onToggleItemCheckedAll(item.id);
      } else {
        onToggleItemCheckedAll(item.id, true);
      }
    };

    return (
      <ShadowBox
        flex={1}
        justifyContent='center'
        borderRadius='xs'
        mt='xs'
        mx={marginHorizontal}
        pl='s'>
        <Box height={48} flexDirection='row' alignItems='center'>
          <Icon
            name={item.isFavorited ? 'RedHeart' : 'EmptyHeart'}
            variant='m'
            onPress={handleToggleFavorite}
          />

          <Text
            flex={1}
            numberOfLines={1}
            mx='s'
            variant='webSmall'
            color='textPrimary'>
            <Highlighter
              highlightStyle={{ color: theme.colors.greenSecondary }}
              autoEscape
              searchWords={[highlightStr ?? '']}
              textToHighlight={item.templateName}
            />
          </Text>
          <TouchableOpacity
            onPress={onPressSelectAll}
            style={{
              height: 24,
              minWidth: 50,
              padding: 4,
              opacity: 1,
              borderRadius: 100,
              backgroundColor:
                selectedCount > 0
                  ? theme.colors.greenSecondaryMild
                  : theme.colors.grey01,
            }}>
            <Box
              backgroundColor={
                selectedCount > 0 ? 'greenSecondaryMild' : 'grey01'
              }
              flexDirection='row'
              style={{
                minWidth: 42,
                alignItems: 'center',
              }}>
              <RadioButton
                iconVariant='s'
                onPress={onPressSelectAll}
                isSelected={selectedCount > 0}
              />
              <Box
                ml={selectedCount > 99 ? 'xxxs' : 'none'}
                minWidth={26}
                justifyContent='center'>
                <Text textAlign='center' variant='webSmall' color='textPrimary'>
                  {selectedCount === 0 || selectedCount === totalItems
                    ? 'All'
                    : selectedCount.toString()}
                </Text>
              </Box>
            </Box>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={handleToggleExpand}
            style={{
              marginHorizontal: 4,
              height: 36,
              width: 48,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Icon
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              variant='l'
              marginLeft='none'
            />
          </TouchableOpacity>
        </Box>

        <AnimatedBox style={animatedContainerStyle}>
          <AnimatedBox style={animatedContentStyle}>
            <Box backgroundColor='grey01' mb='s' borderRadius='xs' mr='s'>
              <Box height={32} borderBottomColor='grey02' borderBottomWidth={1}>
                <Text
                  padding='xs'
                  variant='webSmall'
                  color='textPrimary'
                  numberOfLines={1}
                  textAlign='left'
                  ellipsizeMode='tail'>
                  {item.templateName}
                </Text>
              </Box>

              {renderedItems}
            </Box>
          </AnimatedBox>
        </AnimatedBox>
      </ShadowBox>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.isFavorited === nextProps.item.isFavorited &&
      prevProps.item.itemsChecked.length ===
        nextProps.item.itemsChecked.length &&
      prevProps.item.checklistTemplateItems.length ===
        nextProps.item.checklistTemplateItems.length &&
      prevProps.highlightStr === nextProps.highlightStr
    );
  }
);

const ChecklistSubItem = React.memo(
  ({ id, itemTitle, isChecked, onToggleItemChecked }) => {
    const handlePress = useCallback(() => {
      onToggleItemChecked(id);
    }, [onToggleItemChecked, id]);

    return (
      <Box
        height={40}
        flexDirection='row'
        alignItems='center'
        pr='xs'
        style={{ paddingLeft: 10 }}
        key={id}>
        <TouchableOpacity onPress={handlePress}>
          <CheckBoxButton checked={isChecked} />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={handlePress}
          style={{
            marginLeft: 10,
            flexDirection: 'row',
            flex: 1,
          }}>
          <Text
            variant='bodySecondary'
            color='textPrimary'
            numberOfLines={2}
            ellipsizeMode='tail'
            textAlign='left'
            style={{
              lineHeight: 16,
            }}>
            {itemTitle}
          </Text>
        </TouchableOpacity>
      </Box>
    );
  }
);
